import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getDocumentVerificationHistory, getLeadDocuments } from "services/leadsService";
import {
  getDocumentFolders,
  DOCUMENT_ENTITY_TYPES,
} from "services/documentFolderService";

const fetchUnifiedDocuments = async (params, entityType) => {
  try {
    const fileTypeParam = Array.isArray(params.fileType)
      ? params.fileType.join(",")
      : params.fileType;

    const rawSearch = (params.search || "").trim();
    const activeSearch = rawSearch.length >= 3 ? rawSearch : null;

    const isHistoryView = params.viewType === "history";
    if (isHistoryView && entityType === DOCUMENT_ENTITY_TYPES.LEAD) {
      const response = await getDocumentVerificationHistory({
        leadId: params.entityId,
        folderId: null,
        fileType: fileTypeParam,
        search: activeSearch,
        startDate: params.startDate,
        endDate: params.endDate,
        modifiedStartDate: params.modifiedStartDate,
        modifiedEndDate: params.modifiedEndDate,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      });

      if (response.success) {
        const transformedData = response.data.map((doc) => ({
          id: doc.DocumentId,
          name: doc.FileName,
          fileName: doc.FileName,
          filePath: doc.FilePath,
          size: (doc.FileSize || 0) * 1024,
          fileType: doc.FileName?.split(".").pop()?.toLowerCase() || "unknown",
          uploadedAt: doc.UploadedOn,
          uploadedBy: doc.UploadedByName || "System",
          verificationStatus: doc.VerificationStatus,
          remarks: doc.Remark,
          canApproveOrReject: doc.CanApproveOrReject,
          leadName: doc.LeadName,
          isHistoryItem: true,
          leadId: params.entityId,
          DocumentId: doc.DocumentId,
          FileName: doc.FileName,
          FileExtension: doc.FileExtension,
          FilePath: doc.FilePath,
          LeadName: doc.LeadName,
          History: doc.History || [],
        }));

        return {
          data: transformedData,
          totalCount:
            params.pageNumber === 1 && transformedData.length === 0
              ? 0
              : response.totalRecords ?? 0,
          hasMore: transformedData.length >= params.pageSize,
          folders: [],
          currentFolderId: null,
          parentFolderId: null,
          currentFolderName: "",
        };
      } else {
        throw new Error(response.error || "Failed to fetch document history");
      }
    }

    // Generic DocumentFolders service for LEAD, JOB, GLOBAL/SETTINGS
    const response = await getDocumentFolders({
      entityType: entityType || DOCUMENT_ENTITY_TYPES.LEAD,
      entityId: params.entityId,
      folderId: params.folderId,
      fileType: fileTypeParam,
      search: activeSearch,
      startDate: params.startDate,
      endDate: params.endDate,
      modifiedStartDate: params.modifiedStartDate,
      modifiedEndDate: params.modifiedEndDate,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    });

    if (response.success) {
      let finalData = response.data || [];
      const subfolderIds = new Set((response.folders || []).map((f) => String(f.FolderId || f.folderId || f.id)));
      const hasSearchOrFilters = Boolean(
        params.fileType ||
          params.search ||
          params.startDate ||
          params.endDate ||
          params.modifiedStartDate ||
          params.modifiedEndDate
      );

      // Strictly enforce folder hierarchy when no active search/filters
      if (!hasSearchOrFilters) {
        const currentTargetFolderId = params.folderId ? String(params.folderId) : null;
        
        finalData = (response.data || []).filter((doc) => {
          const docFolderId = doc.folderId ?? doc.FolderId ?? null;
          const docFolderIdStr = docFolderId !== null && docFolderId !== undefined ? String(docFolderId) : null;

          // 1. If document belongs to any subfolder inside current folder, exclude it from current folder level
          if (docFolderIdStr && subfolderIds.has(docFolderIdStr)) {
            return false;
          }

          // 2. If viewing a specific folder, document must match this folder ID (or have no specific folder ID if backend returned it for this folder)
          if (currentTargetFolderId) {
            if (docFolderIdStr) {
              return docFolderIdStr === currentTargetFolderId;
            }
            return true;
          }

          // 3. If viewing root folder, exclude documents that belong to any subfolder/folder
          if (!currentTargetFolderId) {
            if (docFolderIdStr && docFolderIdStr !== "null" && docFolderIdStr !== String(response.currentFolderId)) {
              return false;
            }
          }

          return true;
        });
      }

      // Safe date parser helper
      const getTimestamp = (dateVal) => {
        if (!dateVal) return 0;
        if (typeof dateVal === "number") return dateVal;
        const parsed = new Date(dateVal).getTime();
        return isNaN(parsed) ? 0 : parsed;
      };

      // Sort documents descending by created/uploaded date (newest first)
      finalData.sort((a, b) => {
        const timeA = getTimestamp(a.UploadedOn || a.CreatedOn);
        const timeB = getTimestamp(b.UploadedOn || b.CreatedOn);
        return timeB - timeA;
      });

      // Sort folders descending by created date (newest first)
      const sortedFolders = [...(response.folders || [])].sort((a, b) => {
        const timeA = getTimestamp(a.CreatedOn);
        const timeB = getTimestamp(b.CreatedOn);
        return timeB - timeA;
      });

      return {
        data: finalData,
        totalCount: response.totalRecords ?? finalData.length,
        hasMore: finalData.length >= params.pageSize,
        folders: sortedFolders,
        currentFolderId: response.currentFolderId || null,
        parentFolderId: response.parentFolderId || null,
        currentFolderName: response.currentFolderName || "",
      };
    } else if (entityType === DOCUMENT_ENTITY_TYPES.LEAD) {
      // Legacy fallback for Lead if GetFolders service fails
      const legacyResponse = await getLeadDocuments(params);
      if (legacyResponse.success) {
        const getTimestamp = (dateVal) => {
          if (!dateVal) return 0;
          if (typeof dateVal === "number") return dateVal;
          const parsed = new Date(dateVal).getTime();
          return isNaN(parsed) ? 0 : parsed;
        };

        const transformedData = legacyResponse.data.map((doc) => ({
          id: doc.DocumentId,
          name: doc.FileName,
          fileType: doc.FileName.split(".").pop().toLowerCase(),
          size: (doc.FileSize || 0) * 1024,
          UploadedByName: doc.UploadedByName || "System",
          ModifiedByName: doc.ModifiedByName || null,
          UploadedOn: doc.UploadedOn,
          ModifiedOn: doc.ModifiedOn,
          leadId: params.entityId,
          filePath: doc.FilePath,
          isVerified: doc.IsVerified ?? doc.IsVerfied ?? false,
          docVerificationId: doc.DocVerificationId,
          canApproveOrReject: doc.CanApproveOrReject,
          verificationStatus:
            typeof doc.VerificationStatus === "number"
              ? doc.VerificationStatus
              : parseInt(doc.VerificationStatus ?? 0, 10) || 0,
          verifiedBy:
            doc.VerifiedByName || doc.VerifiedBy || doc.VerifiedById || null,
          verifiedAt: doc.VerifiedAt || doc.VerifiedDate || null,
          verificationRemark:
            doc.Remark || doc.Remarks || doc.VerificationRemark || "",
          isHistoryItem: false,
          folderId: doc.FolderId ?? doc.folderId ?? null,
          FolderId: doc.FolderId ?? doc.folderId ?? null,
        }));

        transformedData.sort((a, b) => {
          const timeA = getTimestamp(a.uploadedAt || a.UploadedOn);
          const timeB = getTimestamp(b.uploadedAt || b.UploadedOn);
          return timeB - timeA;
        });

        return {
          data: transformedData,
          totalCount:
            params.pageNumber === 1 && transformedData.length === 0
              ? 0
              : legacyResponse.totalRecords ?? 0,
          hasMore: transformedData.length >= params.pageSize,
          folders: legacyResponse.folders || [],
          currentFolderId: legacyResponse.currentFolderId || null,
          parentFolderId: legacyResponse.parentFolderId || null,
          currentFolderName: "",
        };
      }
      throw new Error(response.error || legacyResponse.error);
    } else {
      throw new Error(response.error || "Failed to fetch documents");
    }
  } catch (error) {
    console.error("Error fetching unified documents:", error);
    return {
      data: [],
      totalCount: 0,
      hasMore: false,
      folders: [],
      currentFolderId: null,
      parentFolderId: null,
      currentFolderName: "",
    };
  }
};

/**
 * Hook for managing unified entity document folder navigation, search/filtering, and caching.
 * @param {Object} options
 * @param {number} options.entityType - Entity Type enum (1: LEAD, 2: JOB, 3: SETTINGS/GLOBAL)
 * @param {string|number|null} options.entityId - Unique entity identifier (or null for Global Settings)
 * @param {string} [options.initialViewType="documents"] - Initial view ("documents" or "history")
 */
export const useUnifiedDocuments = ({
  entityType = DOCUMENT_ENTITY_TYPES.LEAD,
  entityId = null,
  initialViewType = "documents",
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [parentFolderId, setParentFolderId] = useState(null);
  const [rootFolderId, setRootFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([{ id: null, name: "General" }]);
  const [viewType, setViewType] = useState(initialViewType);

  const folderCacheRef = useRef({});
  const hasInitialDataRef = useRef(false);
  const [hasInitialData, setHasInitialDataState] = useState(false);

  const setHasInitialData = useCallback((value) => {
    hasInitialDataRef.current = value;
    setHasInitialDataState(value);
  }, []);

  const invalidateCache = useCallback((targetFolderId = null) => {
    folderCacheRef.current = {};
  }, []);

  const [filterState, setFilterState] = useState({
    entityId: entityId,
    folderId: null,
    fileType: null,
    search: "",
    startDate: null,
    endDate: null,
    modifiedStartDate: null,
    modifiedEndDate: null,
    pageNumber: 1,
    pageSize: 10,
    viewType: initialViewType,
  });

  const filterStateRef = useRef(filterState);
  useEffect(() => {
    filterStateRef.current = filterState;
  }, [filterState]);

  // Sync entityId or viewType prop changes to filterState
  useEffect(() => {
    setFilterState((prev) => {
      if (prev.entityId === entityId && prev.viewType === initialViewType) return prev;
      return {
        ...prev,
        entityId: entityId,
        viewType: initialViewType,
      };
    });
  }, [entityId, initialViewType]);

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      filterState.fileType ||
        filterState.search ||
        filterState.startDate ||
        filterState.endDate ||
        filterState.modifiedStartDate ||
        filterState.modifiedEndDate
    );
  }, [
    filterState.fileType,
    filterState.search,
    filterState.startDate,
    filterState.endDate,
    filterState.modifiedStartDate,
    filterState.modifiedEndDate,
  ]);

  const loadDocuments = useCallback(
    async (options = {}) => {
      const { forceRefresh = false } = options || {};
      const params = filterStateRef.current;
      // Allow entityId to be null or undefined ONLY when entityType is GLOBAL/SETTINGS
      if (
        !params.entityId &&
        entityType !== DOCUMENT_ENTITY_TYPES.GLOBAL &&
        entityType !== DOCUMENT_ENTITY_TYPES.SETTINGS
      ) {
        return;
      }

      const cacheKey = params.folderId ? String(params.folderId) : "ROOT";
      const isUnfiltered =
        !params.fileType &&
        !params.search &&
        !params.startDate &&
        !params.endDate &&
        !params.modifiedStartDate &&
        !params.modifiedEndDate &&
        params.pageNumber === 1 &&
        params.viewType === "documents";

      if (!forceRefresh && isUnfiltered && folderCacheRef.current[cacheKey]) {
        const cached = folderCacheRef.current[cacheKey];
        setDocuments(cached.data);
        setTotalRecords(cached.totalCount);
        setFolders(cached.folders);
        setCurrentFolderId(cached.currentFolderId);
        setParentFolderId(cached.parentFolderId);
        const hasCachedItems =
          cached.totalCount > 0 || (cached.folders && cached.folders.length > 0);
        if (hasCachedItems && !hasInitialDataRef.current) {
          setHasInitialData(true);
        }
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await fetchUnifiedDocuments(params, entityType);

        // Prevent race conditions
        const currentParams = filterStateRef.current;
        const currentFileTypeStr = Array.isArray(currentParams.fileType)
          ? currentParams.fileType.join(",")
          : currentParams.fileType || "";
        const paramsFileTypeStr = Array.isArray(params.fileType)
          ? params.fileType.join(",")
          : params.fileType || "";

        if (
          currentParams.entityId !== params.entityId ||
          currentParams.folderId !== params.folderId ||
          currentParams.pageNumber !== params.pageNumber ||
          currentParams.pageSize !== params.pageSize ||
          currentFileTypeStr !== paramsFileTypeStr ||
          currentParams.search !== params.search ||
          currentParams.startDate !== params.startDate ||
          currentParams.endDate !== params.endDate ||
          currentParams.modifiedStartDate !== params.modifiedStartDate ||
          currentParams.modifiedEndDate !== params.modifiedEndDate ||
          currentParams.viewType !== params.viewType
        ) {
          return;
        }

        setDocuments(response.data);
        setTotalRecords(response.totalCount);
        setFolders(response.folders);
        setCurrentFolderId(response.currentFolderId);
        setParentFolderId(response.parentFolderId);

        if (response.currentFolderId && !params.folderId) {
          setRootFolderId(response.currentFolderId);
          setFolderPath((prev) => {
            if (prev.length > 0 && prev[0].id === null) {
              const updated = [...prev];
              updated[0] = {
                ...updated[0],
                id: response.currentFolderId,
                name: response.currentFolderName || "General",
              };
              return updated;
            }
            return prev;
          });
        }

        if (isUnfiltered) {
          folderCacheRef.current[cacheKey] = {
            data: response.data,
            totalCount: response.totalCount,
            folders: response.folders,
            currentFolderId: response.currentFolderId,
            parentFolderId: response.parentFolderId,
          };
        }

        const hasFilters =
          params.fileType ||
          params.search ||
          params.startDate ||
          params.endDate ||
          params.modifiedStartDate ||
          params.modifiedEndDate;
        const hasAnyContent =
          response.totalCount > 0 || (response.folders && response.folders.length > 0);

        if (hasAnyContent && !hasFilters && !hasInitialDataRef.current) {
          setHasInitialData(true);
        }
      } catch (err) {
        console.error("Error loading unified documents:", err);
      } finally {
        setLoading(false);
      }
    },
    [entityType, setHasInitialData]
  );

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments, filterState]);

  const handleFilterChange = useCallback((keyOrObj, value) => {
    setFilterState((prev) => {
      let updates = {};
      if (typeof keyOrObj === "object" && keyOrObj !== null) {
        updates = keyOrObj;
      } else {
        updates = { [keyOrObj]: value };
      }

      let hasChanges = false;
      const nextState = { ...prev };

      Object.entries(updates).forEach(([k, val]) => {
        let normVal = Array.isArray(val) ? val.join(",") : val === "" ? null : val;
        if (k === "fileType" && typeof normVal === "string") {
          normVal = normVal.replace(/^\./, "");
        }
        if (prev[k] !== normVal) {
          nextState[k] = normVal;
          hasChanges = true;
        }
      });

      if (!hasChanges) return prev;
      nextState.pageNumber = 1;
      return nextState;
    });
  }, []);

  const handlePageChange = useCallback((page) => {
    setFilterState((prev) => ({ ...prev, pageNumber: page }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterState((prev) => ({
      ...prev,
      fileType: null,
      search: "",
      startDate: null,
      endDate: null,
      modifiedStartDate: null,
      modifiedEndDate: null,
      pageNumber: 1,
    }));
  }, []);

  const switchView = useCallback((newViewType) => {
    setViewType(newViewType);
    setFilterState((prev) => ({
      ...prev,
      viewType: newViewType,
      pageNumber: 1,
      folderId: newViewType === "history" ? null : prev.folderId,
    }));
  }, []);

  const navigateToFolder = useCallback((folderId, folderName) => {
    setFilterState((prev) => ({
      ...prev,
      folderId: folderId,
      pageNumber: 1,
      search: "",
    }));
    setFolderPath((prev) => {
      const existsIndex = prev.findIndex((item) => String(item.id) === String(folderId));
      if (existsIndex !== -1) return prev.slice(0, existsIndex + 1);
      return [...prev, { id: folderId, name: folderName }];
    });
  }, []);

  const navigateToBreadcrumb = useCallback((index) => {
    setFolderPath((prev) => {
      const target = prev[index];
      if (!target) return prev;
      const targetFolderId = index === 0 ? null : target.id;
      setFilterState((fPrev) => ({
        ...fPrev,
        folderId: targetFolderId,
        pageNumber: 1,
        search: "",
      }));
      return prev.slice(0, index + 1);
    });
  }, []);

  const navigateUp = useCallback(() => {
    setFolderPath((prev) => {
      if (prev.length <= 1) return prev;
      const newPath = prev.slice(0, prev.length - 1);
      const parentTarget = newPath[newPath.length - 1];
      const parentFolderIdToNavigate = newPath.length === 1 ? null : parentTarget.id;
      setFilterState((fPrev) => ({
        ...fPrev,
        folderId: parentFolderIdToNavigate,
        pageNumber: 1,
        search: "",
      }));
      return newPath;
    });
  }, []);

  const refetch = useCallback(
    (force = true) => {
      if (force) {
        invalidateCache();
      }
      return loadDocuments({ forceRefresh: force });
    },
    [invalidateCache, loadDocuments]
  );

  const totalPages = useMemo(() => {
    return Math.ceil(totalRecords / filterState.pageSize);
  }, [totalRecords, filterState.pageSize]);

  return {
    documents,
    loading,
    totalRecords,
    hasInitialData,
    hasActiveFilters,
    filterState,
    handleFilterChange,
    handlePageChange,
    clearFilters,
    switchView,
    viewType,
    totalPages,
    refetch,
    invalidateCache,
    folders,
    currentFolderId,
    parentFolderId,
    rootFolderId,
    folderPath,
    navigateToFolder,
    navigateToBreadcrumb,
    navigateUp,
  };
};

export default useUnifiedDocuments;
